import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useApiRequest from "./useApiRequest";

const scopeToCodeString = (scope) =>
  [scope.product_code, scope.resource, scope.subresource, scope.ownership, scope.action].filter(Boolean).join(".");

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const useScopeSearch = ({excludeIds = [], extraParams, limit = 25, debounceMs = 350} = {}) => {
  const [inputValue, setInputValue] = useState("");
  const [term, setTerm] = useState("");
  const [rawOptions, setRawOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestIdRef = useRef(0);
  const rawCountRef = useRef(0);

  const {list} = useApiRequest({baseUrl: "/scopes", resource: "scopes", autoErrorSnackbar: false});

  const listRef = useRef(list);
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  const extraParamsKey = useMemo(() => (extraParams ? JSON.stringify(extraParams) : ""), [extraParams]);

  useEffect(() => {
    const handle = setTimeout(() => setTerm(inputValue.trim()), debounceMs);
    return () => clearTimeout(handle);
  }, [inputValue, debounceMs]);

  const fetchPage = useCallback(
    async ({pageToFetch, append, requestId}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const params = {limit, page: pageToFetch, ...(extraParams || {})};
      if (term) params.q = term;

      const res = await listRef.current(params);
      if (requestId !== requestIdRef.current) return;

      if (res?.success) {
        const items = normalizeArray(res.data);
        const total = res.data?.pagination?.totalItems;
        const loadedSoFar = (append ? rawCountRef.current : 0) + items.length;
        rawCountRef.current = loadedSoFar;
        setRawOptions((prev) => (append ? [...prev, ...items] : items));
        setPage(pageToFetch);
        if (typeof total === "number") {
          setHasMore(loadedSoFar < total && items.length > 0);
        } else {
          setHasMore(items.length === limit);
        }
      } else if (!append) {
        setRawOptions([]);
        rawCountRef.current = 0;
        setHasMore(false);
      }

      if (append) setLoadingMore(false);
      else setLoading(false);
    },
    [limit, term, extraParams]
  );

  useEffect(() => {
    const currentId = ++requestIdRef.current;
    // Cancel any stale pagination state before firing the new search.
    rawCountRef.current = 0;
    setLoadingMore(false);
    setHasMore(false);
    setPage(0);
    fetchPage({pageToFetch: 0, append: false, requestId: currentId});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, extraParamsKey, limit]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const currentId = requestIdRef.current;
    fetchPage({pageToFetch: page + 1, append: true, requestId: currentId});
  }, [loading, loadingMore, hasMore, page, fetchPage]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const options = useMemo(
    () =>
      rawOptions
        .filter((s) => !excludeSet.has(s.id))
        .map((s) => ({id: s.id, label: scopeToCodeString(s) || String(s.id), raw: s})),
    [rawOptions, excludeSet]
  );

  const resetInput = useCallback(() => {
    setInputValue("");
    setTerm("");
  }, []);

  return {inputValue, setInputValue, options, loading, loadingMore, hasMore, loadMore, resetInput};
};

export default useScopeSearch;
